import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Table,
  Button,
  Link,
  StatusIndicator,
  Box,
  SpaceBetween
} from '@cloudscape-design/components';
import { imageLinkApi } from './imageLinkApi.js';

export default function ResultsSection({ refreshTrigger }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadRuns = async () => {
    setLoading(true);
    try {
      const data = await imageLinkApi.listRuns();
      setRuns(data.runs || []);
    } catch (err) {
      console.error('Failed to load runs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRuns(); }, [refreshTrigger]);

  const handleDownload = async (runId) => {
    setDownloadingId(runId);
    try { await imageLinkApi.downloadCsv(runId); }
    catch (err) { console.error('CSV download failed:', err); }
    finally { setDownloadingId(null); }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const columnDefinitions = [
    {
      id: 'timestamp',
      header: 'Upload Time',
      cell: (item) => formatTime(item.timestamp),
      width: 160
    },
    {
      id: 'user',
      header: 'User',
      cell: (item) => item.user || 'unknown',
      width: 100
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => {
        if (item.failed_count > 0) {
          return (
            <SpaceBetween direction="horizontal" size="xxs">
              <StatusIndicator type="success">{item.success_count} ok</StatusIndicator>
              <StatusIndicator type="error">{item.failed_count} failed</StatusIndicator>
            </SpaceBetween>
          );
        }
        return <StatusIndicator type="success">{item.success_count} of {item.total_images} uploaded</StatusIndicator>;
      },
      width: 200
    },
    {
      id: 'sample',
      header: 'Sample Image',
      cell: (item) => {
        if (!item.sample_url) return '—';
        const filename = decodeURIComponent(item.sample_url.split('/').pop());
        const shortName = filename.length > 25 ? filename.slice(0, 23) + '...' : filename;
        return <Link href={item.sample_url} external fontSize="body-s">{shortName}</Link>;
      },
      width: 250
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item) => (
        <Button variant="inline-link" iconName="download"
          loading={downloadingId === item.run_id}
          onClick={() => handleDownload(item.run_id)}>
          CSV
        </Button>
      ),
      width: 100
    }
  ];

  return (
    <Container
      header={
        <Header variant="h2"
          actions={<Button iconName="refresh" onClick={loadRuns} loading={loading}>Refresh</Button>}
        >
          📋 Upload History
        </Header>
      }
    >
      <Table
        columnDefinitions={columnDefinitions}
        items={runs}
        loading={loading}
        loadingText="Loading upload history..."
        empty={
          <Box textAlign="center" color="inherit" padding="l">
            <SpaceBetween size="xxs">
              <Box variant="p" color="inherit"><b>No uploads yet</b></Box>
              <Box variant="p" color="text-body-secondary">Upload images to generate public URLs.</Box>
            </SpaceBetween>
          </Box>
        }
        variant="embedded"
        sortingDisabled
      />
    </Container>
  );
}
